import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Users,
  Shield,
  CalendarDays,
  Trophy,
  ArrowRight,
  Activity,
  Loader2,
  AlertTriangle,
  Megaphone,
  Pin,
} from "lucide-react";

type ClubRole =
  | "dirigeant"
  | "cadre_technique"
  | "viewer"
  | "player_parent"
  | "lecteur";

type TeamRole = "owner" | "coach" | "assistant" | "player" | "parent";

type ClubMemberRow = {
  user_id: string | null;
  email: string | null;
  role: ClubRole;
  status?: string | null;
};

type TeamRow = {
  id: string;
  team_name: string | null;
  league: string | null;
  players?: unknown;
};

type TeamMemberRow = {
  team_id: string;
  user_id: string | null;
  role: TeamRole;
  status?: string | null;
  invited_email?: string | null;
  player_json_id?: string | null;
  player_number?: number | null;
};

type MatchRow = {
  id: string;
  date: string | null;
  team_id: string | null;
  opponent: string | null;
  score: string | null;
  victory: boolean | null;
};

type TeamCard = {
  id: string;
  name: string;
  category?: string;
  coachNames: string[];
  playersCount: number;
};

type MatchCard = {
  id: string;
  date: string;
  teamName: string;
  opponent: string;
  score?: string;
  result?: "W" | "L";
};

type PostRow = {
  id: string;
  title: string | null;
  body: string | null;
  author_user_id: string | null;
  pinned: boolean | null;
  created_at: string | null;
};

type PostCard = {
  id: string;
  title: string;
  content: string;
  authorName: string;
  pinned: boolean;
  createdAt: string;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDisplayNameFromEmail(email?: string | null) {
  if (!email) return "Membre";
  const local = email.split("@")[0]?.trim();
  return local || email;
}

function buildMemberNameMap(rows: ClubMemberRow[]) {
  const map = new Map<string, string>();

  for (const row of rows) {
    if (!row.user_id) continue;
    map.set(row.user_id, getDisplayNameFromEmail(row.email));
  }

  return map;
}

function dedupeStrings(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)));
}

function normalizeTeamName(row: TeamRow) {
  return row.team_name?.trim() || "Équipe sans nom";
}

function normalizeScore(score?: string | null) {
  const clean = score?.trim();
  return clean || undefined;
}

export default function ClubDashboard() {
  const { clubId } = useParams<{ clubId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [myRoles, setMyRoles] = useState<ClubRole[]>([]);
  const [teams, setTeams] = useState<TeamCard[]>([]);
  const [upcomingMatches, setUpcomingMatches] = useState<MatchCard[]>([]);
  const [lastMatches, setLastMatches] = useState<MatchCard[]>([]);
  const [recentPosts, setRecentPosts] = useState<PostCard[]>([]);
  const [membersCount, setMembersCount] = useState(0);
  const [coachesCount, setCoachesCount] = useState(0);
  const [playersCount, setPlayersCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      if (!clubId) {
        setError("Club introuvable.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) throw new Error("Utilisateur non connecté.");

        const clubMembersRes = await supabase
          .from("club_members")
          .select("user_id, email, role, status")
          .eq("club_id", clubId)
          .eq("status", "active");

        if (clubMembersRes.error) throw clubMembersRes.error;

        const clubMembers = (clubMembersRes.data || []) as ClubMemberRow[];
        const memberNameMap = buildMemberNameMap(clubMembers);

        const myClubRoles = clubMembers
          .filter((m) => m.user_id === user.id)
          .map((m) => m.role);

        const postsRes = await supabase
          .from("posts")
          .select("id, title, body, author_user_id, pinned, created_at")
          .eq("club_id", clubId)
          .eq("scope", "club")
          .order("created_at", { ascending: false })
          .limit(2);

        if (postsRes.error) throw postsRes.error;

        const postRows = (postsRes.data || []) as PostRow[];
        const recentPostsUi: PostCard[] = postRows.map((row) => {
          let authorName = "Membre du club";
          if (row.author_user_id === user.id) {
            authorName = "Vous";
          } else if (row.author_user_id) {
            authorName = memberNameMap.get(row.author_user_id) || "Membre du club";
          }

          return {
            id: row.id,
            title: row.title?.trim() || "Sans titre",
            content: row.body?.trim() || "",
            authorName,
            pinned: !!row.pinned,
            createdAt: row.created_at || new Date().toISOString(),
          };
        });

        const teamsRes = await supabase
          .from("teams")
          .select("id, team_name, league, players")
          .eq("club_id", clubId)
          .or("archived.is.null,archived.eq.false")
          .order("team_name", { ascending: true });

        if (teamsRes.error) throw teamsRes.error;

        const teamRows = (teamsRes.data || []) as TeamRow[];
        const teamIds = teamRows.map((t) => t.id);

        let teamMembers: TeamMemberRow[] = [];
        let matchRows: MatchRow[] = [];

        if (teamIds.length > 0) {
          const teamMembersRes = await supabase
            .from("team_members")
            .select(
              "team_id, user_id, role, status, invited_email, player_json_id, player_number"
            )
            .in("team_id", teamIds)
            .eq("status", "active");

          if (teamMembersRes.error) throw teamMembersRes.error;
          teamMembers = (teamMembersRes.data || []) as TeamMemberRow[];

          const matchesRes = await supabase
            .from("matches")
            .select("id, date, team_id, opponent, score, victory")
            .in("team_id", teamIds)
            .order("date", { ascending: false });

          if (matchesRes.error) throw matchesRes.error;
          matchRows = (matchesRes.data || []) as MatchRow[];
        }

        const teamsUi: TeamCard[] = teamRows.map((team) => {
          const teamMemberRows = teamMembers.filter((tm) => tm.team_id === team.id);

          const coachNames = dedupeStrings(
            teamMemberRows
              .filter((tm) => tm.role === "coach" || tm.role === "assistant" || tm.role === "owner")
              .map((tm) => {
                if (tm.user_id && memberNameMap.has(tm.user_id)) {
                  return memberNameMap.get(tm.user_id)!;
                }
                return getDisplayNameFromEmail(tm.invited_email);
              })
          );

          const playersFromTeamMembers = teamMemberRows.filter(
            (tm) => tm.role === "player"
          ).length;

          let fallbackPlayersCount = 0;
          if (Array.isArray(team.players)) {
            fallbackPlayersCount = team.players.length;
          }

          return {
            id: team.id,
            name: normalizeTeamName(team),
            category: team.league || undefined,
            coachNames,
            playersCount:
              playersFromTeamMembers > 0 ? playersFromTeamMembers : fallbackPlayersCount,
          };
        });

        const allCoachUserIds = dedupeStrings(
          teamMembers
            .filter(
              (tm) => tm.role === "coach" || tm.role === "assistant" || tm.role === "owner"
            )
            .map((tm) => tm.user_id || tm.invited_email || "")
        );

        const totalPlayersCount = teamsUi.reduce((acc, t) => acc + t.playersCount, 0);

        const teamNameMap = new Map<string, string>();
        for (const t of teamsUi) {
          teamNameMap.set(t.id, t.name);
        }

        const now = Date.now();

        const mappedMatches = matchRows
          .filter((m) => !!m.id && !!m.date && !!m.team_id)
          .map((m) => {
            const matchDate = m.date || new Date().toISOString();
            const score = normalizeScore(m.score);
            let result: "W" | "L" | undefined;

            if (typeof m.victory === "boolean") {
              result = m.victory ? "W" : "L";
            }

            return {
              id: m.id,
              date: matchDate,
              teamName: teamNameMap.get(m.team_id || "") || "Équipe",
              opponent: m.opponent?.trim() || "Adversaire",
              score,
              result,
            } as MatchCard;
          });

        const upcoming = mappedMatches
          .filter((m) => new Date(m.date).getTime() >= now)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .slice(0, 5);

        const recent = mappedMatches
          .filter((m) => new Date(m.date).getTime() < now)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 5);

        if (cancelled) return;

        setMyRoles(myClubRoles);
        setTeams(teamsUi);
        setUpcomingMatches(upcoming);
        setLastMatches(recent);
        setRecentPosts(recentPostsUi);
        setMembersCount(clubMembers.length);
        setCoachesCount(allCoachUserIds.length);
        setPlayersCount(totalPlayersCount);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Impossible de charger le dashboard du club.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const canManageMembers = useMemo(
    () =>
      myRoles.includes("dirigeant") || myRoles.includes("cadre_technique"),
    [myRoles]
  );

  const canManageTeams = useMemo(
    () =>
      myRoles.includes("dirigeant") || myRoles.includes("cadre_technique"),
    [myRoles]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div className="min-w-0">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Vue globale du club
          </div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Dashboard
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
          {canManageTeams && (
            <Link
              to={`/club/${clubId}/teams`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Shield className="h-4 w-4" />
              Gérer les équipes
              <ArrowRight className="h-4 w-4 opacity-70" />
            </Link>
          )}

          {canManageMembers && (
            <Link
              to={`/club/${clubId}/members`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
            >
              <Users className="h-4 w-4" />
              Inviter des membres
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          <div className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">Membres</div>
            <Users className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {loading ? "—" : membersCount}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Joueurs, coachs, parents…
          </div>
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">Équipes</div>
            <Shield className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {loading ? "—" : teams.length}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Groupes gérés dans le club
          </div>
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">Joueurs</div>
            <Trophy className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {loading ? "—" : playersCount}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Total joueurs sur les équipes
          </div>
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">Coachs</div>
            <Activity className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {loading ? "—" : coachesCount}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Staff encadrant
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="xl:col-span-2 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Équipes</div>
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Aperçu rapide
              </div>
            </div>
            <Link
              to={`/club/${clubId}/teams`}
              className="text-sm text-gray-700 dark:text-gray-200 hover:underline"
            >
              Voir tout
            </Link>
          </div>

          {loading ? (
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement des équipes...
            </div>
          ) : teams.length === 0 ? (
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Aucune équipe dans ce club.
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {teams.map((t) => (
                <div
                  key={t.id}
                  className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {t.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Coachs : {t.coachNames.length ? t.coachNames.join(", ") : "—"} •{" "}
                        {t.playersCount} joueurs
                      </div>
                    </div>

                    {t.category && (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                        {t.category}
                      </span>
                    )}
                  </div>

                  <div className="mt-3">
                    <Link
                      to={`/club/${clubId}/team/${t.id}`}
                      className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100 hover:underline"
                    >
                      Ouvrir l’équipe <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-gray-500" />
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Matchs
            </div>
          </div>

          {loading ? (
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement des matchs...
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  À venir
                </div>
                <div className="mt-2 space-y-2">
                  {upcomingMatches.map((m) => (
                    <div
                      key={m.id}
                      className="rounded-xl border border-gray-200 dark:border-gray-800 p-3"
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {m.teamName} vs {m.opponent}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(m.date)}
                      </div>
                    </div>
                  ))}

                  {upcomingMatches.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Aucun match à venir.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Derniers résultats
                </div>
                <div className="mt-2 space-y-2">
                  {lastMatches.map((m) => (
                    <div
                      key={m.id}
                      className="rounded-xl border border-gray-200 dark:border-gray-800 p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {m.teamName} vs {m.opponent}
                        </div>

                        {m.result && (
                          <span
                            className={cx(
                              "text-xs px-2 py-0.5 rounded-full",
                              m.result === "W"
                                ? "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200"
                                : "bg-red-100 text-red-900 dark:bg-red-900/20 dark:text-red-200"
                            )}
                          >
                            {m.result === "W" ? "V" : "D"}
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(m.date)} {m.score ? `• ${m.score}` : ""}
                      </div>
                    </div>
                  ))}

                  {lastMatches.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Aucun match récent.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <Link
              to={`/club/${clubId}/teams`}
              className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100 hover:underline"
            >
              Voir les équipes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-gray-500" />
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Dernières publications
            </div>
          </div>
          <Link
            to={`/club/${clubId}/posts`}
            className="text-sm text-gray-700 dark:text-gray-200 hover:underline"
          >
            Voir tout
          </Link>
        </div>

        {loading ? (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des publications...
          </div>
        ) : recentPosts.length === 0 ? (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Aucune publication pour le club.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recentPosts.map((p) => (
              <div
                key={p.id}
                className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {p.title}
                  </div>
                  {p.pinned && (
                    <Pin className="h-4 w-4 text-gray-400 shrink-0" />
                  )}
                </div>

                {p.content && (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {p.content}
                  </div>
                )}

                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {p.authorName} • {formatDate(p.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}